import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IAccessAlertAdmin, IAccessAlertChild } from 'src/app/core/models/access';
import { IGuardian } from 'src/app/core/models/user';
import { AccessService } from 'src/app/core/services/auth/access/access.service';
import { selectAccessAlertAdmin } from 'src/app/core/store/access/access.selectors';
import { selectLoggedInGuardian } from 'src/app/core/store/guardian/guardian.selectors';
import { selectAllActiveStudents, selectAllFormerStudents } from 'src/app/core/store/students/student.selectors';
import { LearnerAvatarComponent } from 'src/app/features/learner-profile/components/learner-avatar/learner-avatar.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { ContactAdminComponent } from '../contact-admin/contact-admin.component';

export interface AccessAlertPayload {
  action?: 'granted' | 'removed' | 'mixed' | 'none';
  children?: IAccessAlertChild[];
  isFirstLoginNoLearnersAlert?: boolean;
}

@Component({
  selector: 'app-access-alert',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconComponent, LearnerAvatarComponent],
  templateUrl: './access-alert.component.html',
  styleUrl: './access-alert.component.scss',
})
export class AccessAlertComponent implements OnInit {
  dialog = inject(MatDialog);
  dialogRef = inject<MatDialogRef<AccessAlertComponent, string> | null>(MatDialogRef, { optional: true });
  data = inject<AccessAlertPayload | IGuardian | null>(MAT_DIALOG_DATA, { optional: true });
  router = inject(Router);
  store = inject(Store);
  accessService = inject(AccessService);

  accessResult = output<string>();

  loggedInGuardian = toSignal(this.store.select(selectLoggedInGuardian), { initialValue: null });
  learnerList = this.store.selectSignal(selectAllActiveStudents);
  formerLearnerList = this.store.selectSignal(selectAllFormerStudents);
  accessAlertAdmin = toSignal(this.store.select(selectAccessAlertAdmin), { initialValue: null });
  guardian: IGuardian | IAccessAlertAdmin | null = null;
  adminName = '';
  adminEmail = '';
  action: 'granted' | 'removed' | 'mixed' | 'none' = 'granted';
  isFirstLoginNoLearnersAlert = false;
  private readonly justAcceptedTermsKey = 'myhub.justAcceptedTermsOfUse';
  // Children array containing the learner object and whether their access was revoked
  children: IAccessAlertChild[] = [];

  get grantedChildren(): IAccessAlertChild[] {
    return this.children.filter((child) => !child.accessRevoked);
  }

  get revokedChildren(): IAccessAlertChild[] {
    return this.children.filter((child) => child.accessRevoked);
  }

  get hasGrantedChildren(): boolean {
    return this.grantedChildren.length > 0;
  }

  get hasRevokedChildren(): boolean {
    return this.revokedChildren.length > 0;
  }

  get isMixed(): boolean {
    return this.action === 'mixed';
  }

  get isNone(): boolean {
    return this.action === 'none';
  }

  get showNoLearnersEmptyState(): boolean {
    return this.isNone && !this.isFirstLoginNoLearnersAlert;
  }

  // No active learners remain, but the guardian still has former (inactive) learners.
  // In this case we show a single dedicated message instead of the change/support blocks.
  get hasNoActiveButFormerLearners(): boolean {
    return (this.learnerList()?.length ?? 0) === 0 && (this.formerLearnerList()?.length ?? 0) > 0;
  }

  get description(): string {
    if (this.isFirstLoginNoLearnersAlert) {
      return `${this.adminName} (Admin) has not granted you access to any children linked to their profile`;
    }

    if (this.isNone) {
      return `${this.adminName} (Admin) has updated your children access. There are no learners assigned to your profile.`;
    }

    if (this.isMixed) {
      return `${this.adminName} (Admin) has updated your children access. Here are the changes:`;
    }

    if (this.action === 'removed') {
      return `${this.adminName} (Admin) has removed your access to the following children linked to your account:`;
    }

    return `${this.adminName} (Admin) has granted you access to the following children linked to their profile:`;
  }

  get supportMessageRequiresAccessRequest(): boolean {
    if (this.isFirstLoginNoLearnersAlert) {
      return false;
    }

    return this.action === 'removed' || this.action === 'none';
  }

  ngOnInit() {
    if (!this.data) return;

    // Support either passing IGuardian directly or a payload containing guardian + children
    if (this.isAccessAlertPayload(this.data)) {
      const payload = this.data;
      this.guardian = this.accessAlertAdmin() ?? null;
      if (this.accessAlertAdmin()) {
        this.adminName =
          `${this.accessAlertAdmin()?.firstname}${this.accessAlertAdmin()?.lastname ? ' ' + this.accessAlertAdmin()?.lastname : ''}`.trim();
        this.adminEmail = this.accessAlertAdmin()?.email ?? '';
      }
      if (payload.action) this.action = payload.action;
      if (payload.children) {
        this.children = payload.children;
      }
      this.isFirstLoginNoLearnersAlert = payload.isFirstLoginNoLearnersAlert ?? false;
    } else if (this.isGuardian(this.data)) {
      this.guardian = this.data;
      const g = this.guardian;
      this.adminName = `${g.firstname}${g.lastname ? ' ' + g.lastname : ''}`.trim();
      this.adminEmail = g.email ?? '';
    }

    if (!this.children.length) {
      this.action = 'none';
      return;
    }

    if (this.hasGrantedChildren && this.hasRevokedChildren) {
      this.action = 'mixed';
      return;
    }

    if (this.hasRevokedChildren) {
      this.action = 'removed';
      return;
    }

    this.action = 'granted';
  }

  onUnderstand() {
    if (this.dialogRef) {
      this.onAcknowledge();
      this.dialogRef.close('understand');
    } else {
      this.accessResult.emit('understand');
    }
  }

  onContactAdmin() {
    if (this.dialogRef) {
      this.dialogRef.close('contact-admin');
    } else {
      this.accessResult.emit('contact-admin');
    }

    this.dialog.open(ContactAdminComponent, {
      width: '558px',
    });
  }

  onContactSupport() {
    this.router.navigate(['help']);
    this.dialog.closeAll();
  }

  // Reload only when the learner list is NOT yet up to date:
  // i.e. a granted student is missing from the list, or a revoked student is still on it.
  // If all granted students are already present AND no revoked students remain, the list is
  // current and a reload is unnecessary.
  private shouldReloadAfterAcknowledge(): boolean {
    const currentList = this.learnerList() ?? [];
    const currentIds = new Set(currentList.map((s) => s.id));

    const allGrantedPresent = this.grantedChildren.every((c) => currentIds.has(c.learner.id));
    const noRevokedRemaining = this.revokedChildren.every((c) => !currentIds.has(c.learner.id));

    const listIsUpToDate = allGrantedPresent && noRevokedRemaining;
    return !listIsUpToDate;
  }

  // acknowledge the access change and close the dialog
  onAcknowledge() {
    this.clearJustAcceptedTermsFlag();

    if (this.isFirstLoginNoLearnersAlert) {
      return;
    }

    // use the logged in guardian's ID number to confirm the access change has been acknowledged
    const loggedInGuardian = this.loggedInGuardian();
    if (loggedInGuardian && loggedInGuardian.idPassport) {
      // call the access service to confirm the access change has been acknowledged
      this.accessService.confirmAccessChanges(loggedInGuardian.idPassport).subscribe({
        next: () => {
          if (this.dialogRef) {
            this.dialogRef.close('acknowledged');
          } else {
            this.accessResult.emit('acknowledged');
          }
          if (this.shouldReloadAfterAcknowledge()) {
            window.location.reload();
          }
        },
        error: (error: unknown) => {
          console.error('Failed to acknowledge access change', error);
          // still close the dialog even if the request fails to avoid trapping the user
          if (this.dialogRef) {
            this.dialogRef.close('acknowledged');
          } else {
            this.accessResult.emit('acknowledged');
          }
          if (this.shouldReloadAfterAcknowledge()) {
            window.location.reload();
          }
        },
      });
    } else {
      console.warn('No logged in guardian or ID number found, cannot acknowledge access change');
    }
  }

  onClose() {
    if (this.dialogRef) {
      this.onAcknowledge();
      this.dialogRef.close();
    } else {
      this.accessResult.emit('close');
    }
  }

  private clearJustAcceptedTermsFlag(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(this.justAcceptedTermsKey);
  }

  private isAccessAlertPayload(data: AccessAlertPayload | IGuardian): data is AccessAlertPayload {
    return 'guardian' in data || 'children' in data || 'action' in data || 'isFirstLoginNoLearnersAlert' in data;
  }

  private isGuardian(data: AccessAlertPayload | IGuardian): data is IGuardian {
    return 'id' in data;
  }
}
